/**
 * usePermissions — reads permission keys from Redux (workspace bootstrap / auth).
 * Keys match backend `Permission.key` strings (e.g. branch.timetable.edit).
 */

import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/app/store'
import {
  getUserType,
  getContextType,
  hasResourcePermission,
  getResourcePermissions,
  isUserType,
  isInContext,
  decodeJWT,
  PERMISSION_PATTERNS,
  USER_TYPES,
  CONTEXT_TYPES
} from '@/utils/permissionUtils'
import { buildPermissionGrantStableKey, hasPermission as evaluateGrant } from '@/core/permissions/permissionResolver'

export function usePermissions() {
  // Read permissions directly from Redux store (reactive)
  const permissionsState = useSelector((state: RootState) => state.permissions)
  const user = useSelector((state: RootState) => state.user.user)

  const rawPermissions = permissionsState.permissions ?? []
  const permissionsStableKey = buildPermissionGrantStableKey(rawPermissions)
  const permissions = useMemo(
    () => rawPermissions,
     
    [permissionsStableKey]
  )
  const userType = permissionsState.userType || getUserType()
  const contextType = permissionsState.contextType || getContextType()
  const isLoading = permissionsState.isLoading || false
  const isRBACReady = permissionsState.isRBACReady === true

  const hasPermission = useCallback(
    (permission: string): boolean => evaluateGrant(permission, permissions),
    [permissions]
  )

  const refreshPermissions = () => {
    // Permissions are managed by Redux - no manual refresh needed
    // This function is kept for API compatibility but does nothing
  }

  return {
    // State (from Redux)
    permissions,
    userType,
    contextType,
    isLoading,
    isRBACReady,

    // User context from store
    user,
    
    // Permission checking functions (use Redux data)
    hasPermission,
    hasResourcePermission,
    getResourcePermissions,
    isUserType,
    isInContext,
    
    // Constants
    PERMISSION_PATTERNS,
    USER_TYPES,
    CONTEXT_TYPES,
    
    // Utility functions
    decodeJWT,
    refreshPermissions, // Deprecated - kept for API compatibility
  }
}

/**
 * Hook for checking a specific permission
 */
export function usePermission(permission: string) {
  const { hasPermission: checkPermission, isLoading } = usePermissions()
  
  return {
    hasPermission: checkPermission(permission),
    isLoading
  }
}

/**
 * Hook for checking resource permissions
 */
export function useResourcePermissions(resource: string, context: string | null = null) {
  const { getResourcePermissions: getResourcePerms, isLoading } = usePermissions()
  
  return {
    permissions: getResourcePerms(resource, context),
    isLoading
  }
}

